import Toolbar from '../../../../common/components/toolbar/Toolbar';
import {ButtonConfig} from '../../../../../../controllers/dialog/ButtonConfig';
import {tAsString} from '../../../../../../common/helpers/react/text/tAsString';
import {tWithHtml} from '../../../../../../common/helpers/react/text/tWithHtml';
import {ButtonSize, MessageStatus} from '@sabre/spark-react-core/types';
import {cssClasses} from '../../../../../../common/helpers/browser/cssClasses';
import {useControllers} from '../../../../../../common/helpers/react/hooks/useControllers';
import {DefaultTableCheckboxModeProps} from '../../../../common/components/table/DefaultTable';
import {asNumber} from '../../../../../../common/helpers/converters/asNumber';
import {ModalButtons} from '../../../../../../controllers/dialog/ModalButtons';
import {Checkbox} from '@sabre/spark-react-core';
import React from 'react';
import {HTML_ID} from '../../../../../../common/helpers/id/HTML_ID';
import {firstValueFrom} from 'rxjs';

export default function LockGroupsToolbar(props: DefaultTableCheckboxModeProps) {
    const controllers = useControllers();
    const http = controllers.http;
    const dialog = controllers.dialog;
    const groups = controllers.groups;

    const data = groups.getAllGroups$().useState() ?? [];
    const dataCount = data.length;
    const checkboxId = HTML_ID();

    const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (dataCount !== 0) {
            if (e.target.checked) {
                props.setCheckedRows(data.map(o => o.id));
            } else {
                props.setCheckedRows([]);
            }
        } else {
            dialog.showNotification({
                status: MessageStatus.WARNING,
                content: tAsString('NO_ROWS_TO_SELECT'),
                stick_to: checkboxId
            }).andWeAreDone();
        }
    };

    const showLockModal = async (lock: boolean) => {
        if (props.checkedRows.length === 0) {
            dialog.showNotification({
                status: MessageStatus.WARNING,
                content: tAsString('SELECT_SOME_OPTIONS')
            }).andWeAreDone();
            return;
        }

        const firstName = data.find(g => g.id === props.checkedRows[0])?.name;
        const confirmLabel = tAsString(lock ? 'LOCK' : 'UNLOCK');

        await dialog.showModal({
            title: tAsString(lock ? 'LOCK_GROUPS' : 'UNLOCK_GROUPS'),
            children: tWithHtml(lock ? 'X_SELECTED_GROUPS_WILL_BE_LOCKED' : 'X_SELECTED_GROUPS_WILL_BE_UNLOCKED', {
                count: props.checkedRows.length,
                name: firstName
            }),
            buttons: [
                ModalButtons.CANCEL(dialog),
                {
                    children: confirmLabel,
                    ariaLabel: confirmLabel,
                    className: cssClasses(lock ? 'qa-lock-groups-confirm-button' : 'qa-unlock-groups-confirm-button'),
                    onClick: async () => {
                        const count = props.checkedRows.length;

                        await dialog.takeNotifiedAction({
                            action: async () => {
                                const allAgentIds: string[] = [];

                                for (const groupId of props.checkedRows) {
                                    await groups.refreshGroupAssignees(asNumber(groupId));
                                    const groupAgents = await firstValueFrom(groups.getGroupAgents$(asNumber(groupId)));
                                    allAgentIds.push(...groupAgents.map(ga => ga.agent));
                                }

                                await controllers.agents.lockAgents$(allAgentIds, lock);
                            },
                            successMessage: tAsString(lock ? 'X_SELECTED_GROUPS_HAVE_BEEN_LOCKED' : 'X_SELECTED_GROUPS_HAVE_BEEN_UNLOCKED', {
                                count,
                                name: firstName
                            }),
                            errorMessage: tAsString(lock ? 'X_SELECTED_GROUPS_LOCK_FAILED' : 'X_SELECTED_GROUPS_UNLOCK_FAILED', {
                                count,
                                name: firstName
                            }),
                            finalizer: async () => {
                                props.setCheckedRows([]);
                                await dialog.closeModal();
                                await http.navigateTo('groups');
                            }
                        });
                    }
                }
            ]
        });
    };

    const toolbarButtons: ButtonConfig[] = [
        {
            className: cssClasses('aat-autokey-escape', 'qa-lock-groups-cancel-button'),
            secondary: true,
            children: tAsString('CANCEL'),
            size: ButtonSize.SMALL,
            onClick: () => http.navigateTo('groups')
        },
        {
            className: cssClasses('qa-unlock-groups-button'),
            children: tAsString('UNLOCK'),
            size: ButtonSize.SMALL,
            onClick: () => showLockModal(false)
        },
        {
            className: cssClasses('qa-lock-groups-button'),
            children: tAsString('LOCK'),
            size: ButtonSize.SMALL,
            onClick: () => showLockModal(true)
        }
    ];

    const toolbarContent = <Checkbox
        label={tWithHtml('X_OF_Y_SELECTED', {
            count: props.checkedRows.length,
            total: dataCount
        })}
        checked={!!dataCount && props.checkedRows.length === dataCount}
        onChange={onChange}
        className={checkboxId}
    />;

    return <Toolbar
        content={toolbarContent}
        buttons={toolbarButtons}
    />;
}
